import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import {
  supabase,
  signInWithPassword as supabaseSignInWithPassword,
  requestPasswordReset as supabaseRequestPasswordReset,
  verifyEmailLinkToken as supabaseVerifyEmailLinkToken,
  setPassword as supabaseSetPassword,
  requestEmailOtp,
  verifyEmailOtp,
  signOut as supabaseSignOut,
} from '../lib/supabase';
import { clearImpersonation } from '../lib/impersonationPreview';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  verifyEmailLinkToken: (
    tokenHash: string,
    type: 'recovery' | 'invite' | 'magiclink' | 'signup' | 'email_change',
  ) => Promise<void>;
  setPassword: (password: string) => Promise<void>;
  requestOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Hydrate from any persisted session.
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data.session ?? null);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setLoading(false);
      });

    // Subscribe to subsequent auth changes (sign-in, sign-out, token refresh).
    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession ?? null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    await supabaseSignInWithPassword(email, password);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await supabaseRequestPasswordReset(email);
  }, []);

  const verifyEmailLinkToken = useCallback(
    async (
      tokenHash: string,
      type: 'recovery' | 'invite' | 'magiclink' | 'signup' | 'email_change',
    ) => {
      await supabaseVerifyEmailLinkToken(tokenHash, type);
    },
    [],
  );

  const setPassword = useCallback(async (password: string) => {
    await supabaseSetPassword(password);
  }, []);

  const requestOtp = useCallback(async (email: string) => {
    await requestEmailOtp(email);
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string) => {
    await verifyEmailOtp(email, code);
  }, []);

  const signOut = useCallback(async () => {
    // sessionStorage outlives the Supabase session, so a staff "view as
    // client" preview would otherwise follow the next sign-in in this tab.
    clearImpersonation();
    await supabaseSignOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      session,
      loading,
      signInWithPassword,
      requestPasswordReset,
      verifyEmailLinkToken,
      setPassword,
      requestOtp,
      verifyOtp,
      signOut,
    }),
    [
      session,
      loading,
      signInWithPassword,
      requestPasswordReset,
      verifyEmailLinkToken,
      setPassword,
      requestOtp,
      verifyOtp,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
